/**
 * 園ごとの色（gardens.theme_color）から、Tailwindのorange-50〜900と同じ形の
 * 濃淡パレットを作る。
 *
 * 🟥 各園のDBには既に theme_color が入っている（箕面=オレンジ、
 *    泉中央・多賀城=青、さぬま=水色、かがの=青緑、六丁の目=藍…と園ごとに違う）。
 *    ページ側は今までこれを一度も読んでおらず、全園オレンジのTailwind色（orange-*）を
 *    直書きしていた（2026-09-25 後藤さん「デザインを青く変えてたの、作りかけてたんだぞ」）。
 * 🟥 CSS変数にして <div style={theme}> に載せ、クラス名は bg-[var(--theme-500)] のように
 *    「変数を指す固定文字列」にする。Tailwindは文字列を見てクラスを生成するので、
 *    実行時に決まる色そのものをクラス名には書けない。変数越しにすることで、
 *    ビルド時に生成される1つのクラスが、実行時にどの園の色にでもなる。
 */

const STEPS: Record<number, number> = {
  50: 97,
  100: 93,
  200: 85,
  300: 74,
  400: 62,
  500: 53, // 入力の明度で上書きする（後述）。ここは入力が無いときの既定値
  600: 44,
  700: 36,
  800: 29,
  900: 22,
};

function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const m = hex.replace('#', '');
  const r = parseInt(m.slice(0, 2), 16) / 255;
  const g = parseInt(m.slice(2, 4), 16) / 255;
  const b = parseInt(m.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      default:
        h = (r - g) / d + 4;
    }
    h *= 60;
  }
  return { h, s: s * 100, l: l * 100 };
}

function hslToHex(h: number, s: number, l: number): string {
  const sl = Math.max(0, Math.min(100, s)) / 100;
  const ll = Math.max(0, Math.min(100, l)) / 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = sl * Math.min(ll, 1 - ll);
  const f = (n: number) => ll - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  const toHex = (v: number) =>
    Math.round(v * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(f(0))}${toHex(f(8))}${toHex(f(4))}`;
}

export type ThemeVars = Record<`--theme-${number}`, string> & { '--theme': string };

export function buildThemeScale(hex: string): ThemeVars {
  const { h, s, l } = hexToHsl(hex);
  // 500段を入力の実際の明度に合わせ、他の段はそこからの相対で作る
  // （50〜900はTailwindの並びと同じ意味を保つ：50が最も明るく、900が最も暗い）
  const base = Math.max(20, Math.min(80, l));
  const ratio = base / STEPS[500];

  const vars = {} as ThemeVars;
  for (const [step, target] of Object.entries(STEPS)) {
    const n = Number(step);
    const L = n === 500 ? base : Math.max(6, Math.min(97, target * ratio));
    vars[`--theme-${n}` as const] = hslToHex(h, s, L);
  }
  vars['--theme'] = hex;
  return vars;
}
